import { useState } from 'react'
import { Box, Flex, Grid, Input, Spinner } from '@chakra-ui/react'
import { LuSearch as Search, LuCheck as Check, LuUserPlus as UserPlus } from 'react-icons/lu'
import {
  useFormColors,
  FormLabel,
  DatePicker,
  Form,
  TrackedInput,
  TrackedPhoneInput,
  withFormProvider,
  type WithFormProvider,
} from '@minetrace/ui/forms'
import { Modal, toaster, Text, useT } from '@minetrace/ui'
import { personsService } from '../../api/services'
import type { PersonResponse } from '../../api/models/response/person'
import type { CreateDriverRequest } from '../../api/models/request/transportista'
import { PersonFieldLengths, DriverFieldLengths } from '../../lib/fieldLengths'
import { useDriverPersonForm, driverPersonDefaults, type DriverPersonFormValues } from './useDriverPersonForm'

// Alta de persona nueva (conductor): formulario aislado con el estándar del
// proyecto — schema vía hook + HOC `withFormProvider`, y campos `Tracked*` que
// derivan label/obligatoriedad/error/contador del propio schema. El `required`
// sale solo (los campos no `.optional()` del schema), sin cablear nada a mano.
function DriverPersonFormInner({
  submitting,
  onCreate,
}: { submitting: boolean; onCreate: (values: DriverPersonFormValues) => void } & WithFormProvider<DriverPersonFormValues>) {
  const c = useFormColors()
  const { t } = useT()
  // El input por defecto (recipe) es relleno gris claro SIN borde, que casi no se
  // distingue sobre la tarjeta. Le añadimos un borde visible (M3 outlined) — los
  // Tracked* reenvían estos props de estilo al Input. El foco resalta en verde marca.
  const field = {
    border: '1px solid',
    borderColor: c.border,
    _hover: { borderColor: c.textDimmed },
    _focus: { borderColor: c.accent },
  } as const
  return (
    <Form<DriverPersonFormValues> onSubmit={onCreate}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
        <TrackedInput name="names" labelTid="transportistas.nuevo.driverExtras.names" maxLength={PersonFieldLengths.names} {...field} />
        <TrackedInput name="paternalSurname" labelTid="transportistas.nuevo.driverExtras.paternal" maxLength={PersonFieldLengths.paternalSurname} {...field} />
        <TrackedInput name="maternalSurname" labelTid="transportistas.nuevo.driverExtras.maternal" maxLength={PersonFieldLengths.maternalSurname} {...field} />
        <TrackedInput name="email" type="email" labelTid="transportistas.nuevo.driverExtras.email" maxLength={PersonFieldLengths.email} {...field} />
        <TrackedPhoneInput name="phoneMobile" labelTid="transportistas.nuevo.driverExtras.phone" maxLength={PersonFieldLengths.phoneMobile} {...field} />
      </Grid>
      <Flex justifyContent="flex-end" mt={1}>
        <Box as="button" type="submit" display="flex" alignItems="center" gap={2} px={4} py={2} bg={c.accent} color="white" borderRadius="lg" fontSize="sm" fontWeight="600" cursor="pointer" opacity={submitting ? 0.6 : 1} _hover={{ opacity: 0.9 }}>
          {submitting ? <Spinner size="sm" color="white" /> : <UserPlus size={14} />}
          {t('transportistas.nuevo.driverExtras.createAndUse')}
        </Box>
      </Flex>
    </Form>
  )
}

const DriverPersonForm = withFormProvider(DriverPersonFormInner, useDriverPersonForm)

export interface DriverLocal extends CreateDriverRequest {
  fullName: string
  identityDoc: string
  /** El conductor ya tiene cuenta de acceso (persistida) o se solicitó crearla. */
  hasAccount?: boolean
}

// Categorías de licencia de conducir (MTC). A-IIIb / A-IIIc son las habituales para carga pesada.
const LICENSE_CATEGORIES = ['A-I', 'A-IIa', 'A-IIb', 'A-IIIa', 'A-IIIb', 'A-IIIc', 'B-I', 'B-IIa', 'B-IIb', 'B-IIc']

interface DriverModalProps {
  open: boolean
  onClose: () => void
  onSave: (driver: DriverLocal) => void
  initial?: DriverLocal | null
  /** PersonIds ya agregados como conductores en este transportista (excluye el que se edita). */
  existingPersonIds?: string[]
}

export default function DriverModal({ open, onClose, onSave, initial, existingPersonIds = [] }: DriverModalProps) {
  const c = useFormColors()
  const { t } = useT()

  const [searchDoc, setSearchDoc] = useState('')
  const [foundPerson, setFoundPerson] = useState<PersonResponse | null>(null)
  const [searching, setSearching] = useState(false)

  // Alta de persona nueva cuando el documento no existe (replica el flujo de Contactos).
  const [searchedDoc, setSearchedDoc] = useState('')
  const [creating, setCreating] = useState(false)
  const [creatingPerson, setCreatingPerson] = useState(false)
  // Valores con que se siembra el formulario de alta (prellenado desde RENIEC/SUNAT).
  const [createPrefill, setCreatePrefill] = useState<DriverPersonFormValues>(driverPersonDefaults)

  const [license, setLicense] = useState(() => ({
    licenseNumber: initial?.licenseNumber ?? '',
    licenseCategory: initial?.licenseCategory ?? '',
    licenseExpiryDate: initial?.licenseExpiryDate ? initial.licenseExpiryDate.split('T')[0]! : '',
  }))

  // Persona elegida (modo edición o tras búsqueda). Sembrada al montar; el padre
  // remonta el modal con `key` en cada apertura para resetear el estado.
  const [picked, setPicked] = useState<{ personId: string; fullName: string; identityDoc: string } | null>(
    () => (initial ? { personId: initial.personId, fullName: initial.fullName, identityDoc: initial.identityDoc } : null),
  )

  const alreadyHasAccount = Boolean(initial?.hasAccount)
  const [createAccount, setCreateAccount] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')

  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const enterCreateMode = (doc: string, prefill: DriverPersonFormValues) => {
    setSearchedDoc(doc)
    setCreatePrefill(prefill)
    setCreating(true)
  }

  const handleSearch = async () => {
    const doc = searchDoc.trim()
    if (!doc) return
    setFoundPerson(null)
    setCreating(false)
    setSearching(true)
    try {
      if (doc.length === 8 && /^\d+$/.test(doc)) {
        const lookup = await personsService.lookupDni(doc)
        if (lookup.foundInDatabase && lookup.personId) {
          const full = await personsService.getById(lookup.personId)
          setFoundPerson(full)
        } else if (lookup.source === 'sunat') {
          // RENIEC/SUNAT tiene el DNI pero no está en BD: pre-llenamos y dejamos crear.
          enterCreateMode(doc, {
            names: lookup.names ?? '',
            paternalSurname: lookup.paternalSurname ?? '',
            maternalSurname: lookup.maternalSurname ?? '',
            email: '',
            phoneMobile: '',
          })
        } else {
          enterCreateMode(doc, driverPersonDefaults)
        }
      } else {
        const data = await personsService.searchByDoc(doc)
        if (data) setFoundPerson(data)
        else enterCreateMode(doc, driverPersonDefaults)
      }
    } catch {
      enterCreateMode(doc, driverPersonDefaults)
    } finally {
      setSearching(false)
    }
  }

  const handlePick = () => {
    if (!foundPerson) return
    // Defensa: no permitir elegir a un conductor que ya está en este transportista.
    if (existingPersonIds.includes(foundPerson.id)) {
      toaster.create({ type: 'warning', description: t('transportistas.nuevo.driverExtras.duplicate') })
      return
    }
    setPicked({ personId: foundPerson.id, fullName: foundPerson.fullName, identityDoc: foundPerson.identityDoc })
    setFoundPerson(null)
    setSearchDoc('')
  }

  // Recibe los valores ya validados por el form (RHF + Zod) e invoca el alta.
  const handleCreatePerson = async (values: DriverPersonFormValues) => {
    setCreatingPerson(true)
    try {
      const documentType = searchedDoc.length === 8 && /^\d+$/.test(searchedDoc) ? 1 : 2
      const person = await personsService.createStep1({
        documentType,
        identityDoc: searchedDoc,
        names: values.names.trim(),
        paternalSurname: values.paternalSurname.trim(),
        maternalSurname: values.maternalSurname?.trim() || undefined,
        email: values.email.trim(),
        phoneMobile: values.phoneMobile.trim(),
      })
      setPicked({ personId: person.id, fullName: person.fullName, identityDoc: person.identityDoc })
      setCreating(false)
      setSearchDoc('')
    } catch (e) {
      toaster.error({ description: e instanceof Error && e.message ? e.message : t('transportistas.nuevo.driverExtras.createError') })
    } finally {
      setCreatingPerson(false)
    }
  }

  const handleSave = () => {
    if (!picked) {
      toaster.error({ description: t('transportistas.nuevo.driverExtras.pickRequired') })
      return
    }
    const wantsAccount = createAccount && !alreadyHasAccount
    if (wantsAccount) {
      if (!username.trim() || !password) {
        toaster.error({ description: t('transportistas.nuevo.driverExtras.credentialsRequired') })
        return
      }
      if (password !== passwordConfirm) {
        toaster.error({ description: t('transportistas.nuevo.driverExtras.passwordMismatch') })
        return
      }
    }
    onSave({
      personId: picked.personId,
      fullName: picked.fullName,
      identityDoc: picked.identityDoc,
      licenseNumber: license.licenseNumber || undefined,
      licenseCategory: license.licenseCategory || undefined,
      licenseExpiryDate: license.licenseExpiryDate || undefined,
      createAccount: wantsAccount ? true : undefined,
      username: wantsAccount ? username.trim() : undefined,
      password: wantsAccount ? password : undefined,
      hasAccount: alreadyHasAccount || wantsAccount,
    })
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t('transportistas.nuevo.driverExtras.editTitle') : t('transportistas.nuevo.driverExtras.addTitle')}
      size="lg"
      footer={
        <Flex gap={3} justifyContent="flex-end">
          <Box as="button" px={4} py={2} borderRadius="lg" border="1px solid" borderColor={c.border} color={c.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={onClose}>
            {t('common.cancel')}
          </Box>
          <Box as="button" px={4} py={2} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor="pointer" opacity={picked ? 1 : 0.5} _hover={{ opacity: picked ? 0.9 : 0.5 }} onClick={handleSave}>
            {t('common.save')}
          </Box>
        </Flex>
      }
    >
      {/* Búsqueda de persona (solo en alta y mientras no haya una elegida) */}
      {!initial && !picked && (
        <Box mb={4}>
          <FormLabel>{t('transportistas.nuevo.fields.identityDoc')}</FormLabel>
          <Flex gap={2}>
            <Input
              value={searchDoc}
              onChange={(e) => setSearchDoc(e.target.value.slice(0, PersonFieldLengths.identityDoc))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch() } }}
              placeholder={t('transportistas.nuevo.fields.identityDocPlaceholder')}
              maxLength={PersonFieldLengths.identityDoc}
              {...inputStyles}
            />
            <Box
              as="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={4}
              bg={c.accentGradient}
              color="white"
              borderRadius="xl"
              cursor="pointer"
              flexShrink={0}
              minW="48px"
              opacity={!searchDoc.trim() || searching ? 0.5 : 1}
              onClick={handleSearch}
            >
              {searching ? <Spinner size="sm" color="white" /> : <Search size={18} />}
            </Box>
          </Flex>

          {foundPerson && (() => {
            const isDup = existingPersonIds.includes(foundPerson.id)
            return (
              <Box bg={isDup ? 'rgba(212, 154, 42, 0.10)' : `${c.accent}10`} border="1px solid" borderColor={isDup ? 'rgba(212, 154, 42, 0.4)' : `${c.accent}30`} borderRadius="xl" p={3} mt={3}>
                <Flex justifyContent="space-between" alignItems="center" gap={3}>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{foundPerson.fullName}</Text>
                    <Text fontSize="xs" color={c.textMuted}>
                      {foundPerson.identityDoc}
                      {foundPerson.email ? ` • ${foundPerson.email}` : ''}
                    </Text>
                  </Box>
                  {isDup ? (
                    <Text fontSize="xs" fontWeight="600" color="#8a5f0b" flexShrink={0} textAlign="right">
                      {t('transportistas.nuevo.driverExtras.duplicate')}
                    </Text>
                  ) : (
                    <Box as="button" display="flex" alignItems="center" gap={2} px={3} py={1.5} bg={c.accent} color="white" borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" flexShrink={0} onClick={handlePick}>
                      <Check size={14} />
                      {t('transportistas.nuevo.driverExtras.select')}
                    </Box>
                  )}
                </Flex>
              </Box>
            )
          })()}

          {/* Persona no encontrada → crear aquí (como Contactos). Tarjeta blanca con
              borde: así los inputs (gris claro) contrastan y no se pierden con el fondo. */}
          {creating && (
            <Box bg={c.bgCard} border="1px solid" borderColor={c.border} borderRadius="xl" p={4} mt={3}>
              <Text fontSize="sm" fontWeight="600" color={c.text}>{t('transportistas.nuevo.driverExtras.createTitle')}</Text>
              <Text fontSize="xs" color={c.textMuted} mb={3}>{t('transportistas.nuevo.driverExtras.createHint')}</Text>
              <DriverPersonForm key={searchedDoc} defaultValues={createPrefill} submitting={creatingPerson} onCreate={handleCreatePerson} />
            </Box>
          )}
        </Box>
      )}

      {/* Persona elegida + licencia */}
      {picked && (
        <>
          <Box bg={c.bgHover} borderRadius="xl" p={3} mb={4}>
            <Text fontSize="sm" fontWeight="600" color={c.text}>{picked.fullName}</Text>
            <Text fontSize="xs" color={c.textMuted}>{picked.identityDoc}</Text>
          </Box>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
            <Box>
              <FormLabel>{t('transportistas.nuevo.fields.licenseNumber')}</FormLabel>
              <Input
                placeholder={t('transportistas.nuevo.fields.licenseNumberPlaceholder')}
                value={license.licenseNumber}
                onChange={(e) => setLicense((p) => ({ ...p, licenseNumber: e.target.value.slice(0, DriverFieldLengths.licenseNumber) }))}
                maxLength={DriverFieldLengths.licenseNumber}
                {...inputStyles}
              />
            </Box>
            <Box>
              <FormLabel>{t('transportistas.nuevo.fields.licenseCategory')}</FormLabel>
              <select
                value={license.licenseCategory}
                onChange={(e) => setLicense((p) => ({ ...p, licenseCategory: e.target.value }))}
                style={selectStyles}
              >
                <option value="">{t('transportistas.nuevo.fields.licenseCategoryPlaceholder')}</option>
                {LICENSE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </Box>
            <Box>
              <FormLabel>{t('transportistas.nuevo.fields.licenseExpiry')}</FormLabel>
              <DatePicker
                value={license.licenseExpiryDate}
                onChange={(v) => setLicense((p) => ({ ...p, licenseExpiryDate: v }))}
              />
            </Box>
          </Grid>

          {/* Cuenta de acceso del conductor */}
          {alreadyHasAccount ? (
            <Box mt={4} bg={`${c.accent}10`} border="1px solid" borderColor={`${c.accent}30`} borderRadius="xl" p={3}>
              <Text fontSize="sm" fontWeight="600" color={c.text}>
                {t('transportistas.nuevo.driverExtras.alreadyHasAccount')}
              </Text>
              {initial?.username && <Text fontSize="xs" color={c.textMuted}>{initial.username}</Text>}
            </Box>
          ) : (
            <Box mt={4}>
              <Flex as="label" alignItems="center" gap={2} cursor="pointer">
                <input type="checkbox" checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} />
                <Text fontSize="sm" color={c.text}>{t('transportistas.nuevo.driverExtras.createAccount')}</Text>
              </Flex>
              {createAccount && (
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mt={3}>
                  <Box>
                    <FormLabel>{t('transportistas.nuevo.driverExtras.username')}</FormLabel>
                    <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" {...inputStyles} />
                  </Box>
                  <Box>
                    <FormLabel>{t('transportistas.nuevo.driverExtras.password')}</FormLabel>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" {...inputStyles} />
                  </Box>
                  <Box>
                    <FormLabel>{t('transportistas.nuevo.driverExtras.passwordConfirm')}</FormLabel>
                    <Input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} autoComplete="new-password" {...inputStyles} />
                  </Box>
                </Grid>
              )}
            </Box>
          )}
        </>
      )}
    </Modal>
  )
}
